"use client";

import { LegalPageLayout } from "@/components/legal/legal-page-layout";

export default function AccountDeletionPage() {
  return (
    <LegalPageLayout title="Account Deletion" lastUpdated="22 August 2026">
      <div className="prose prose-neutral max-w-none space-y-6 text-neutral-700">

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            How to Delete Your Account
          </h2>
          <p>
            To delete your GrowthSync account, please follow these steps:
          </p>
          <ol className="list-decimal pl-6 space-y-3 mt-4">
            <li>Open the GrowthSync app on your device</li>
            <li>Navigate to <strong>Settings</strong></li>
            <li>Go to <strong>Account</strong></li>
            <li>Tap <strong>Delete Account</strong></li>
            <li>Type <strong className="font-mono text-red-600">DELETE</strong> to confirm</li>
            <li>Confirm the deletion</li>
          </ol>
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 mt-6">
            <p className="text-sm text-amber-900">
              <strong>Important:</strong> Account deletion is permanent and cannot be undone. Please ensure you have exported any data you wish to keep before proceeding.
            </p>
          </div>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            What Data Will Be Deleted
          </h2>
          <p>
            When you delete your GrowthSync account, the following data will be permanently removed:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li><strong>Profile information:</strong> Your name, bio, profile photo, headline, location, contact details, links, date of birth and any identity photo you uploaded</li>
            <li><strong>User content:</strong> Posts, comments, reactions, and any other content you've created</li>
            <li><strong>Event data:</strong> Your event RSVPs and event-related content</li>
            <li><strong>Community data:</strong> Your community memberships, posts, and contributions</li>
            <li><strong>Account settings:</strong> All preferences and configuration settings</li>
          </ul>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            What Stays, and Why
          </h2>
          <p>
            Two things are kept rather than deleted, because they are not yours alone:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>
              <strong>Messages and connections:</strong> a conversation belongs to both people in it.
              Deleting your account removes your name and profile from it, so the other person keeps
              their own history but sees only &quot;Deleted User&quot;. Your messages are never shown
              to anyone new, and your profile can no longer be found, viewed or contacted.
            </li>
            <li>
              <strong>Events you hosted:</strong> these are cancelled rather than erased, so anyone
              who bought a ticket keeps their record of the booking and their refund rights.
            </li>
          </ul>
          <p className="mt-4">
            If you want your messages removed from another person&apos;s history as well, email us and
            we will handle it as an erasure request.
          </p>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            Data Retention
          </h2>
          <p>
            While most of your data will be deleted immediately, some information may be retained temporarily for legal, security, or operational purposes, including:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>Transaction records (for tax and accounting compliance, typically retained for 6 years)</li>
            <li>Security logs and audit trails (for fraud prevention and security investigations)</li>
            <li>Data required by law enforcement or regulatory authorities</li>
          </ul>
          <p className="mt-4">
            Any retained data will be handled in accordance with our Privacy Policy and applicable data protection laws.
          </p>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            Subscriptions and Payments
          </h2>
          <p>
            If you have an active subscription, deleting your account will cancel your subscription. However, you may still be charged for the current billing period. We recommend canceling your subscription separately before deleting your account if you wish to avoid any charges.
          </p>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            Need Help?
          </h2>
          <p>
            If you have questions about account deletion or need assistance, please contact us at:
          </p>
          <p className="mt-2">
            <strong>Email:</strong> <a href="mailto:contact@growth-sync.com" className="text-blue-600 hover:text-blue-700 hover:underline">contact@growth-sync.com</a><br />
            <strong>Company:</strong> GrowthSync Network Ltd<br />
            <strong>Registered Office:</strong> Unit 6, Balfour Court, Leyland, PR25 2TF, United Kingdom
          </p>
        </section>

        <section>
          <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">
            Additional Information
          </h2>
          <p>
            For more information about how we handle your data, please review our{" "}
            <a href="/privacy" className="text-blue-600 hover:text-blue-700 hover:underline">Privacy Policy</a> and{" "}
            <a href="/terms" className="text-blue-600 hover:text-blue-700 hover:underline">Terms of Service</a>.
          </p>
        </section>
      </div>
    </LegalPageLayout>
  );
}
